/**
 * 图片处理工具
 */
import { Downloadfile } from '@/api/InteractiveData'
/**
 * 获取图片URL（从服务器下载并转换为Blob URL）
 * @param {string} filePath - 图片路径
 * @param {boolean} cache - 是否使用缓存
 * @returns {Promise<string>} 图片URL
 */
export async function getImageUrl(filePath, cache = true) {
  if (!filePath) {
    console.warn('图片路径为空')
    return ''
  }
  
  // 检查缓存
  if (cache && imageCache[filePath]) {    
    return imageCache[filePath]
  }
  
  try {
    const response = await Downloadfile(filePath)    
    if (response.status != 200) {
      console.error('获取图片数据失败')
      return ''
    }
    
    // 创建Blob对象
    const blob = new Blob([response.data], { 
      type: response.headers['content-type'] || 'image/*' 
    })
    
    // 生成Blob URL
    const imageUrl = window.URL.createObjectURL(blob)
    
    // 缓存URL
    if (cache) {
      imageCache[filePath] = imageUrl
      
      // 清理过期的URL
      setTimeout(() => {
        window.URL.revokeObjectURL(imageUrl)
        delete imageCache[filePath]
      }, 5 * 60 * 1000) // 5分钟后清理
    }    
    return imageUrl
  } catch (error) {
    console.error('获取图片URL失败:', error)
    return ''
  }
}


/**
 * 批量获取图片URL
 * @param {Array<string>} filePaths - 图片路径数组
 * @returns {Promise<Object>} 路径到URL的映射对象
 */
export async function getImageUrls(filePaths) {
  const urlMap = {}
  const promises = []
  
  for (const filePath of filePaths) {
    if (filePath) {
      promises.push(
        getImageUrl(filePath)
          .then(url => {
            urlMap[filePath] = url
          })
          .catch(() => {
            urlMap[filePath] = ''
          })
      )
    }
  }
  
  await Promise.all(promises)
  return urlMap
}

/**
 * 清理图片缓存
 * @param {string} filePath - 指定清理的路径，为空则清理所有
 */
export function clearImageCache(filePath = null) {
  if (filePath) {
    if (imageCache[filePath]) {
      window.URL.revokeObjectURL(imageCache[filePath])
      delete imageCache[filePath]
    }
  } else {
    // 清理所有
    Object.values(imageCache).forEach(url => {
      window.URL.revokeObjectURL(url)
    })
    Object.keys(imageCache).forEach(key => {
      delete imageCache[key]
    })
  }
}

/**
 * Blob转Base64
 * @param {Blob|any} blob - Blob数据
 * @returns {Promise<string>} Base64字符串
 */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    if (!(blob instanceof Blob)) {
      // 如果不是Blob，尝试创建
      blob = new Blob([blob], { type: 'image/png' })
    }
    
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/**
 * 清理Blob URL
 * @param {string} url - Blob URL
 */
export function revokeObjectURL(url) {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}

/**
 * 图片缓存管理器
 */
class ImageCacheManager {
  constructor() {
    this.cache = new Map()
  }
  
  async getImage(apiFunc, filePath, useBase64 = false) {
    const cacheKey = `${filePath}_${useBase64}`
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)
    }
    
    try {
      const url = await getImageUrl(apiFunc, filePath, { useBase64 })
      this.cache.set(cacheKey, url)
      return url
    } catch (error) {
      return null
    }
  }
  
  clearCache() {
    // 清理所有Blob URL
    for (const url of this.cache.values()) {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url)
      }
    }
    this.cache.clear()
  }
  
  removeImage(filePath) {
    const base64Key = `${filePath}_true`
    const blobKey = `${filePath}_false`
    
    if (this.cache.has(blobKey)) {
      const url = this.cache.get(blobKey)
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url)
      }
    }
    
    this.cache.delete(base64Key)
    this.cache.delete(blobKey)
  }
}

// 导出单例实例
export const imageCache = new ImageCacheManager()

export default {
  getImageUrl,
  getImageUrls,
  blobToBase64,
  revokeObjectURL,
  imageCache
}